/**
 * The signed-in session: tokens, the gateway connection, sign-out.
 * Status is honest: "ready" only once the gateway has sent READY.
 */
import {
  ChannelType,
  MessageFlags,
  RelationshipType,
  type AuthSessionResponse,
  type GatewayDispatch,
  type GatewayPresenceUpdateData,
  type Message,
  type NxSessionEndedData,
  type Relationship,
  type SettableStatus,
} from '@neosocial/protocol';
import { tr } from '../i18n';
import { settlePending } from './messages';
import { create } from 'zustand';
import { api, NetworkError, onSessionExpired, tokens } from '../lib/api';
import { assetUrl, desktopBridge, isMobileApp, wsUrl } from '../lib/config';
import { mobileAppVersion, mobilePlatform, unregisterPush } from '../lib/mobile';
import { GatewayClient } from '../lib/gateway/client';
import { messageNotification } from '../notify/message';
import { notify } from '../notify/notify';
import { leaveVoice } from '../voice/controller';
import { useVoice } from '../voice/store';
import { toast } from '../ui/overlay';
import { feed, resetData, setConnection, useData } from './data';
import { feedCalls, resetCalls } from './calls';
import { feedUm, isIgnored, resetUm, setIgnoredList, setRecovery } from './um';
import { onUmDispatch } from './umEvents';
import { feedEmoji } from './emoji';
import { handleConfigDispatch } from './config';
import { applyAccountLook, forgetPendingLook, showDeviceLook } from './look';
import { handleStaffNoticeDispatch } from '../shell/StaffNotices';
import { onNotifyDispatch } from '../notify/wire';
import { mentionKindsOf, messageNotifiesHere } from '../notify/rules';
import { feedEvents } from '../events/store';
import { reportedActivities, setActivitySender } from '../social/activity';
import { noteRestored } from '../social/removal';
import { feedVoiceStatus } from './voiceStatus';
import { isSelfExit } from './selfExit';

export type SessionStatus =
  /** Looking for a saved session. */
  | 'checking'
  | 'signed_out'
  /** Signed in, waiting for READY. */
  | 'connecting'
  | 'ready'
  /** A saved session exists but the server can't be reached. */
  | 'offline';

interface SessionStore {
  status: SessionStatus;
  /** Recovery codes shown once right after sign-up. */
  recoveryCodes: string[] | null;
  /**
   * Why the server ended this session (suspended, disabled, being deleted),
   * from NX_SESSION_ENDED: the sign-in screen says so. Null otherwise.
   */
  endedReason: NxSessionEndedData | null;
  set(p: Partial<Omit<SessionStore, 'set'>>): void;
}

export const useSession = create<SessionStore>((set) => ({
  status: 'checking',
  recoveryCodes: null,
  endedReason: null,
  set: (p) => set(p),
}));

let gateway: GatewayClient | null = null;

export function gatewayClient(): GatewayClient | null {
  return gateway;
}

// Rich presence (social/activity.ts) sends its op 3 on this connection, when the game changes.
setActivitySender((activities) => {
  if (!gateway) return false;
  const status = useData.getState().settings?.status ?? 'online';
  gateway.sendPresence({ since: null, activities, status, afk: false });
  return true;
});

function presence(): GatewayPresenceUpdateData | undefined {
  const st = useData.getState().settings?.status;
  if (!st) return undefined;
  // the desktop's detected game rides along when shared (social/activity.ts)
  return { since: null, activities: reportedActivities(), status: st, afk: false };
}

function connectGateway(): void {
  gateway?.close();
  const bridge = desktopBridge();
  const g = new GatewayClient({
    url: () => wsUrl(''),
    token: async (fresh) => {
      if (fresh) {
        try {
          return (await api.client.refreshOnce()) ? tokens.access() : null;
        } catch {
          return tokens.access();
        }
      }
      try {
        return await api.client.accessToken();
      } catch {
        return tokens.access();
      }
    },
    properties: isMobileApp()
      ? {
          os: mobilePlatform() === 'ios' ? 'iOS' : 'Android',
          browser: mobilePlatform() === 'ios' ? 'Neosocial iOS' : 'Neosocial Android',
          device: '',
          client: mobilePlatform(),
          app_version: mobileAppVersion(),
        }
      : {
          os: typeof navigator !== 'undefined' ? navigator.platform || 'unknown' : 'unknown',
          browser: bridge ? 'Neosocial Desktop' : 'Neosocial Web',
          device: '',
          client: bridge ? 'desktop' : 'web',
          app_version: bridge?.appVersion ?? '0.1.0',
        },
    presence,
  });
  gateway = g;
  g.onStatus((s) => {
    setConnection(s);
    if (s === 'auth_failed') void signOut(true);
  });
  g.onDispatch(feedSessionFrame);
  g.connect();
}

/**
 * Everything a gateway dispatch updates: the data, user management, config,
 * the account's theme and language, and the session itself.
 */
export function feedSessionFrame(frame: GatewayDispatch): void {
  const removedFrom = removedFromGuild(frame);
  feed(frame);
  // DM calls (2026-11-01)
  feedCalls(frame);
  // voice channel statuses (2026-11-01)
  feedVoiceStatus(frame);
  if (removedFrom) onRemovedFromGuild(removedFrom);
  feedUm(frame);
  feedEmoji(frame);
  onUmDispatch(frame);
  // scheduled events (2026-10-29): their own store, the start notice
  feedEvents(frame);
  // the operator's console: flags, banners and maintenance, live
  handleConfigDispatch(frame);
  // staff warnings for you (shown until acknowledged)
  handleStaffNoticeDispatch(frame);
  // notification settings: a server joined while connected brings its own
  onNotifyDispatch(frame);
  if (frame.t === 'NX_SESSION_ENDED') useSession.getState().set({ endedReason: frame.d });
  // the theme follows the account, on every device and tab (ST, SN-B11)
  if (frame.t === 'READY') applyAccountLook(frame.d.user_settings, 'ready');
  if (frame.t === 'USER_SETTINGS_UPDATE') applyAccountLook(useData.getState().settings, 'update');
  if (frame.t === 'READY') {
    if (frame.d.nx_ignored_user_ids === undefined)
      void api.um
        .ignores()
        .then((list) => setIgnoredList(list.map((u) => u.id)))
        .catch(() => {});
    // READY does not carry a running recovery of this account; ask for it
    void api.um
      .recoveryPending()
      .then(setRecovery)
      .catch(() => {});
  }
  if (frame.t === 'MESSAGE_CREATE') {
    settlePending(frame.d);
    notifyIfNeeded(frame.d);
  }
  if (frame.t === 'RELATIONSHIP_ADD') notifyFriendRequest(frame.d);
  if (frame.t === 'READY' && useSession.getState().status !== 'ready')
    useSession.getState().set({ status: 'ready' });
}

/**
 * The guild this frame removes the signed-in user from (kick, ban, leave),
 * read before it is gone. `own`: this device left or deleted it (Leave
 * Server already said "You left …"), or it was already forgotten here.
 */
function removedFromGuild(
  frame: GatewayDispatch,
): { id: string; name: string; own: boolean } | null {
  if (frame.t !== 'GUILD_MEMBER_REMOVE') return null;
  const d = useData.getState();
  if (!d.me || frame.d.user.id !== d.me.id) return null;
  const g = d.guilds[frame.d.guild_id];
  return { id: frame.d.guild_id, name: g?.name ?? '', own: !g || isSelfExit(frame.d.guild_id) };
}

/** Out of the server: out of its voice too, and told so calmly (the screen says it as well). */
function onRemovedFromGuild(g: { id: string; name: string; own: boolean }): void {
  if (useVoice.getState().session.guildId === g.id) leaveVoice();
  if (g.own) return;
  const { t } = tr();
  toast(t('ns.hub.removed', { name: g.name }), { icon: 'alert', tone: 'warn', duration: 9000 });
}

/**
 * Which new messages deserve a notification: a DM, and in a server what the
 * notification settings let through (notify/rules.ts `messageNotifiesHere`:
 * a mention of you, of a role you hold or of @everyone that counts there, a
 * reply to you that pings you (the server puts you in `mentions` then), or
 * any message at All messages in a channel that is not muted; a thread only
 * once joined unless it names you). Never a silent message (@silent),
 * someone you ignore, or a message request (user-management §3.2, §3.3).
 * How it is delivered (focus, DND, bursts, preview, sound, the pop-up and
 * its switch) is notify/notify.ts.
 */
function notifyIfNeeded(m: Message): void {
  const d = useData.getState();
  if (!d.settings || !d.me || m.author.id === d.me.id) return;
  if (isIgnored(m.author.id)) return;
  // Discord's @silent: no notification, no sound
  if ((m.flags & MessageFlags.SUPPRESS_NOTIFICATIONS) !== 0) return;
  const c = d.channels[m.channel_id];
  if (c?.nx_request_state && c.nx_request_state !== 'accepted') return;
  // the notification settings decide (levels, mutes, threads)
  if (!messageNotifiesHere(d, m, c)) return;
  const me = d.me.id;
  const dm = c?.type === ChannelType.DM || c?.type === ChannelType.GROUP_DM;
  const pinged = m.mentions.some((u) => u.id === me);
  const reply = pinged && m.referenced_message?.author.id === me;
  const kind = dm ? 'dm' : reply ? 'reply' : mentionKindsOf(d, m, c).length ? 'mention' : 'message';
  notify(messageNotification(m, kind));
}

/** A friend request waiting for your answer (Friends > Requests). */
function notifyFriendRequest(r: Relationship): void {
  if (r.type !== RelationshipType.PENDING_INCOMING) return;
  const who = r.user.global_name || r.user.username;
  notify({
    kind: 'friend_request',
    title: who,
    body: tr().t('nt.friendRequest'),
    from: r.id,
    icon: assetUrl(r.user.avatar ?? null),
    route: { to: 'requests' },
    tag: 'fr:' + r.id,
  });
}

/** After sign-in, sign-up or a two-step code. */
export function startSession(res: AuthSessionResponse): void {
  tokens.save(res);
  useSession.getState().set({
    status: 'connecting',
    recoveryCodes: res.recovery_codes?.length ? res.recovery_codes : null,
    endedReason: null,
  });
  // signing in again ended the account's own Delete or Disable account: say so
  noteRestored(res.nx_restored);
  connectGateway();
}

/** On load: a saved refresh token means a session to resume. */
export async function restoreSession(): Promise<void> {
  if (!tokens.refresh()) {
    useSession.getState().set({ status: 'signed_out' });
    return;
  }
  try {
    const ok = await api.client.refreshOnce();
    if (!ok) {
      // the start showed the account's look (the refused token is gone): the device's own again
      showDeviceLook();
      useSession.getState().set({ status: 'signed_out' });
      return;
    }
  } catch (e) {
    if (e instanceof NetworkError) {
      useSession.getState().set({ status: 'offline' });
      return;
    }
    useSession.getState().set({ status: 'offline' });
    return;
  }
  useSession.getState().set({ status: 'connecting' });
  connectGateway();
}

export async function signOut(expired = false): Promise<void> {
  // Signing out of no session (Change server on the sign-in screen) must not
  // mark the sign-in screen's own choices as an account's: the language picked
  // there is for whoever signs in next (QA 2026-10-28, desktop).
  const hadSession =
    expired ||
    tokens.refresh() !== null ||
    tokens.access() !== null ||
    useSession.getState().status !== 'signed_out';
  leaveVoice();
  if (!expired && hadSession) {
    // the phone app: no more pushes here for this account (first, while the session works)
    if (isMobileApp()) await unregisterPush();
    try {
      await api.auth.logout();
    } catch {
      /* the session ends here either way */
    }
  }
  gateway?.close();
  gateway = null;
  tokens.clear();
  if (hadSession) forgetPendingLook();
  // the device's own look again: its choice, else the console's default
  showDeviceLook();
  resetData();
  resetUm();
  resetCalls();
  useSession.getState().set({ status: 'signed_out', recoveryCodes: null });
}

export function sendPresence(status: SettableStatus): void {
  gateway?.sendPresence({ since: null, activities: reportedActivities(), status, afk: false });
}

onSessionExpired(() => {
  void signOut(true);
});
